import { json, type LoaderFunction } from '@remix-run/node'
import { useLoaderData, Link } from '@remix-run/react'
import { getRaceBySlug } from '#app/utils/contentful.server'
import { type RaceEntry } from '#app/utils/contentful.types'

export const loader: LoaderFunction = async ({ params }) => {
	const race = await getRaceBySlug(params.raceSlug ?? '')
	if (!race) {
		throw new Response('Not Found', { status: 404 })
	}
	return json({ race })
}

function isVideo(url: string): boolean {
	return url.toLowerCase().endsWith('.mp4')
}

export default function RaceDetails() {
	const { race } = useLoaderData<{ race: RaceEntry }>()

	return (
		<div className="mx-auto max-w-7xl p-6">
			<h1 className="mb-8 text-center text-4xl font-bold">{race.raceName}</h1>
			<div className="flex flex-col gap-8 md:flex-row">
				<div className="md:w-2/3">
					<section className="mb-8">
						<h2 className="mb-4 text-2xl font-semibold">Race Details</h2>
						<div className="grid grid-cols-2 gap-4">
							<p>
								<strong>Date:</strong>{' '}
								{new Date(race.raceDate ?? '').toLocaleDateString('en-US', {
									dateStyle: 'long',
								})}
							</p>
							<p>
								<strong>Finish Time:</strong> {race.finishTime}
							</p>
							<p>
								<strong>Age Group Finish:</strong> {race.ageGroupFinish}
							</p>
							<p>
								<strong>Overall Finish:</strong> {race.overallFinish}
							</p>
						</div>
					</section>

					<section className="mb-8">
						<h2 className="mb-4 text-2xl font-semibold">Race Notes</h2>
						<p className="whitespace-pre-wrap">{race.raceNotes}</p>
					</section>

					{race.racePicturesCollection?.items &&
						race.racePicturesCollection.items.length > 0 && (
							<section>
								<h2 className="mb-4 text-2xl font-semibold">Race Media</h2>
								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									{race.racePicturesCollection.items.map((media, index) => (
										<div
											key={index}
											className="h-64 w-full overflow-hidden rounded-lg"
										>
											{isVideo(media.url ?? '') ? (
												<video
													controls
													className="h-full w-full object-cover"
													src={media.url ?? ''}
												>
													Your browser does not support the video tag.
												</video>
											) : (
												<img
													src={media.url ?? ''}
													alt={media.title || `Race media ${index + 1}`}
													className="h-full w-full object-cover"
												/>
											)}
										</div>
									))}
								</div>
							</section>
						)}
				</div>

				<div className="md:w-1/3">
					{race.stravaEmbed && (
						<section className="mb-8">
							<h2 className="mb-4 text-2xl font-semibold">Strava Activity</h2>
							<div dangerouslySetInnerHTML={{ __html: race.stravaEmbed }} />
						</section>
					)}
					{race.stravaLink && (
						<a
							href={race.stravaLink}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-block rounded bg-orange-500 px-4 py-2 text-white hover:bg-orange-600"
						>
							View on Strava
						</a>
					)}
				</div>
			</div>
			<div className="mt-12 text-center">
				<Link
					to="/running"
					className="inline-block rounded bg-blue-500 px-6 py-3 text-white hover:bg-blue-600"
				>
					Back to Running Page
				</Link>
			</div>
		</div>
	)
}
